import { identity } from 'lodash-es'
import { useCallback, useMemo, useRef } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import type { LabeledOption } from '@fv/client-components/combobox'
import { addressFuncs } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import type { AddressLookupContext, AddressType } from '@fv/models'

import type { AddressWithCompany } from '@/features/addresses/addressFuncs'
import {
  type AddressSearch,
  useAddressSearch,
  useCacheFormattedAddress,
} from '@/features/addresses/useAddressSearch'

import { ShipperAutocomplete } from './ShipperAutocomplete'
import {
  blueOptionClassName,
  defaultComboboxOptionClassName,
  type ShipperClassNames,
} from './ShipperComboBox'
import { ShipperTypeahead } from './ShipperTypeahead'

export type AddressOption = LabeledOption<ClientAddressLookupResult>
type InputLocation = AddressWithCompany
export type AddressInputProps = {
  addressType?: AddressType
  allowCustomInput?: boolean
  autoFocus?: boolean
  cacheFormattedLocation?: boolean
  error?: string
  filterOptions?: (options: AddressOption[]) => AddressOption[]
  formatAddress?: (location: InputLocation) => string
  formatOptionLabel?: (location: InputLocation) => string
  includeAddressBook?: boolean
  includeAtlas?: boolean
  isClearable?: boolean
  location: InputLocation
  name: string
  onInputChange?: (value: string) => void
  onLocationChange: (location: ClientAddressLookupResult | null) => void
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  context?: AddressLookupContext
}

export const AddressInput = ({
  addressType,
  allowCustomInput,
  autoFocus,
  cacheFormattedLocation = true,
  error,
  filterOptions = identity,
  formatAddress = addressFuncs.companyAddress,
  formatOptionLabel,
  includeAddressBook = false,
  includeAtlas = true,
  isClearable = true,
  location,
  name,
  onInputChange,
  onLocationChange,
  placeholder,
  readOnly,
  required = false,
  context,
}: AddressInputProps) => {
  const shouldAllowCustomInput = allowCustomInput ?? false
  const [search, setSearch] = useDebounceValue('', 500)
  const postalCodeSearch = location?.postalCode?.trim() ?? ''
  const effectiveSearch = search.trim() || postalCodeSearch

  const filter: AddressSearch = {
    search: effectiveSearch,
    includeAddressBook,
    includeAtlas,
    type: addressType,
    context,
  }
  const filterRef = useRef(filter)
  filterRef.current = filter

  const cacheFormattedAddress = useCacheFormattedAddress()
  const addressSearchQuery = useAddressSearch(filter)

  const options = useMemo(
    () =>
      (addressSearchQuery.data ?? []).map(v => ({
        label: formatAddress(v),
        value: v,
      })),
    [addressSearchQuery.data, formatAddress],
  )

  const filteredOptions = useMemo(
    () => filterOptions(options),
    [filterOptions, options],
  )

  const selectedOption: AddressOption | null = useMemo(() => {
    const label = formatAddress(location)
    if (!label) return null
    return {
      label,
      value: location as unknown as ClientAddressLookupResult,
    }
  }, [location, formatAddress])

  const handleInputChange = useCallback(
    (value: string) => {
      onInputChange?.(value)
      setSearch(value)
    },
    [onInputChange, setSearch],
  )

  const handleSelect = useCallback(
    (option: AddressOption | null) => {
      const nextLocation = option?.value ?? null
      if (nextLocation && cacheFormattedLocation) {
        const formatted = formatAddress(nextLocation)
        cacheFormattedAddress(
          {
            ...filterRef.current,
            search: formatted,
          },
          nextLocation,
        )
      }
      onLocationChange(nextLocation)
    },
    [
      cacheFormattedLocation,
      formatAddress,
      cacheFormattedAddress,
      onLocationChange,
    ],
  )

  const renderOptionFn = useMemo(
    () =>
      formatOptionLabel
        ? (option: AddressOption) => formatOptionLabel(option.value)
        : undefined,
    [formatOptionLabel],
  )

  if (shouldAllowCustomInput) {
    return (
      <ShipperAutocomplete
        classNames={addressInputClassNames}
        error={error}
        isClearable={isClearable}
        isLoading={addressSearchQuery.isFetching}
        name={name}
        autoFocus={autoFocus}
        onSelect={handleSelect}
        onValueChange={handleInputChange}
        options={filteredOptions}
        getOptionLabel={getAddressOptionLabel}
        getOptionValue={getOptionValue}
        filter={null}
        renderOption={renderOptionFn}
        placeholder={placeholder}
        readOnly={readOnly}
        required={required}
        value={selectedOption?.label ?? ''}
      />
    )
  }

  return (
    <ShipperTypeahead
      classNames={addressInputClassNames}
      error={error}
      isClearable={isClearable}
      isLoading={addressSearchQuery.isFetching}
      name={name}
      autoFocus={autoFocus}
      onSelect={handleSelect}
      onInputValueChange={handleInputChange}
      filter={null}
      options={filteredOptions}
      getOptionValue={getOptionValue}
      renderOption={renderOptionFn}
      placeholder={placeholder}
      readOnly={readOnly}
      required={required}
      value={selectedOption}
    />
  )
}

const addressInputClassNames: ShipperClassNames<AddressOption> = {
  container: 'w-full',
  option: option => {
    const className = defaultComboboxOptionClassName
    if (option.value?.accountLocationId)
      return `${className} border-fv-orange bg-fv-orange-25 hover:border-fv-orange-bright hover:bg-fv-orange-50 data-[highlighted]:bg-fv-orange-50 data-[highlighted]:border-fv-orange-bright`
    return blueOptionClassName
  },
}

const getAddressOptionLabel = (o: AddressOption) => o.label

const getOptionValue = (o: AddressOption) => {
  if (o.value?._id) return o.value?._id
  if (o.value?.city && o.value?.state && o.value?.postalCode)
    return `${o.value?.city}, ${o.value?.state}, ${o.value?.postalCode}`
  return o.label
}
